import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import api from "../../../lib/api";
import { API } from "../../../App";

// Deals Board v2 — Phase 1
// Centralised data hook: fetch deals/contacts/users, expose toolbar/filter/sort state,
// derive aging/activity metadata so columns/cards stay dumb.

const SORT_OPTIONS = [
  { value: "updated_at", label: "Last Updated" },
  { value: "value", label: "Deal Value" },
  { value: "probability", label: "Probability" },
  { value: "expected_close", label: "Expected Close Date" },
  { value: "created_at", label: "Created Date" },
];

// Aging tiers (calendar days since last_activity_at — falls back to created_at).
// ≤7 normal, 8-14 amber stalled, 15-21 orange aging, >21 red dormant.
const AGING_TIERS = {
  normal: { label: "Active", color: "#10B981", maxDays: 7 },
  stalled: { label: "Stalled", color: "#F59E0B", maxDays: 14 },
  aging: { label: "Aging", color: "#EA580C", maxDays: 21 },
  dormant: { label: "Dormant", color: "#DC2626", maxDays: Infinity },
};

const daysBetween = (isoOrDate) => {
  if (!isoOrDate) return null;
  const t = typeof isoOrDate === "string" ? Date.parse(isoOrDate) : new Date(isoOrDate).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
};

const agingTier = (days) => {
  if (days == null) return null;
  if (days <= AGING_TIERS.normal.maxDays) return { key: "normal", ...AGING_TIERS.normal, days };
  if (days <= AGING_TIERS.stalled.maxDays) return { key: "stalled", ...AGING_TIERS.stalled, days };
  if (days <= AGING_TIERS.aging.maxDays) return { key: "aging", ...AGING_TIERS.aging, days };
  return { key: "dormant", ...AGING_TIERS.dormant, days };
};

export function useDealsPipeline(user) {
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    assigned_to_id: "",
    manager_id: "",
    stage: "",
    date_range: "",
    date_basis: "created_at",
    from_date: "",
    to_date: "",
  });
  const [sort, setSort] = useState({ by: "updated_at", dir: "desc" });

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("requester_role", user?.role || "");
      params.append("requester_name", user?.name || "");
      if (search.trim()) params.append("q", search.trim());
      if (filters.assigned_to_id) params.append("assigned_to_id", filters.assigned_to_id);
      if (filters.manager_id) params.append("manager_id", filters.manager_id);
      if (filters.stage) params.append("stage", filters.stage);
      // Phase 2 — date range params (only sent when active).
      if (filters.date_range) {
        params.append("date_range", filters.date_range);
        params.append("date_basis", filters.date_basis || "created_at");
        if (filters.date_range === "custom") {
          if (filters.from_date) params.append("from_date", filters.from_date);
          if (filters.to_date) params.append("to_date", filters.to_date);
        }
      }
      if (sort.by) params.append("sort_by", sort.by);
      if (sort.dir) params.append("sort_dir", sort.dir);
      const res = await api.get(`${API}/crm/deals?${params.toString()}`);
      setDeals(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.name, search, filters, sort]);

  const fetchContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_name: user?.name || "",
      });
      const res = await api.get(`${API}/crm/contacts?${params.toString()}`);
      setContacts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setContacts([]);
    }
  }, [user?.role, user?.name]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get(`/users`);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  useEffect(() => {
    fetchContacts();
    fetchUsers();
  }, [fetchContacts, fetchUsers]);

  // Decorate each deal with derived aging + follow-up overdue flags.
  const decoratedDeals = useMemo(() => {
    return deals.map((d) => {
      const activityIso = d.last_activity_at || d.updated_at || (d.created_at && (typeof d.created_at === "string" ? d.created_at : new Date(d.created_at).toISOString()));
      const ageDays = daysBetween(activityIso);
      const aging = agingTier(ageDays);
      // Follow-up overdue if expected_close is set and in the past for open deals.
      const isOpen = d.stage !== "closed_won" && d.stage !== "closed_lost";
      let followupOverdue = false;
      if (isOpen && d.expected_close) {
        const t = Date.parse(d.expected_close);
        if (Number.isFinite(t) && t < Date.now()) followupOverdue = true;
      }
      return { ...d, _activityIso: activityIso, _ageDays: ageDays, _aging: aging, _followupOverdue: followupOverdue };
    });
  }, [deals]);

  const bdeOptions = useMemo(
    () => users.filter((u) => u.role === "bde" || u.role === "bdm").map((u) => ({ id: u.user_id, name: u.name, role: u.role })),
    [users]
  );

  const managerOptions = useMemo(
    () => users.filter((u) => ["manager", "bdm", "marketing_head", "md", "admin"].includes(u.role)).map((u) => ({ id: u.user_id, name: u.name, role: u.role })),
    [users]
  );

  return {
    deals: decoratedDeals,
    rawDeals: deals,
    contacts,
    users,
    loading,
    search,
    setSearch,
    filters,
    setFilters,
    sort,
    setSort,
    refresh: fetchDeals,
    bdeOptions,
    managerOptions,
    SORT_OPTIONS,
    AGING_TIERS,
  };
}

export { SORT_OPTIONS, AGING_TIERS };
